import type { FilingPeriodForChart } from '@/utils/institutionalHoldersChartData';

/** Max width for a full-density holder column. */
export const INSTITUTIONAL_HOLDERS_COLUMN_WIDTH_PX = 176;

export type InstitutionalHoldersChartDensityMode = 'full' | 'compact' | 'minimal';

/** Quarter band narrower than this → minimal (pill + count). */
export const INSTITUTIONAL_DENSITY_MINIMAL_MAX_PX = 70;
/** Quarter band narrower than this → compact chips (top movers only). */
export const INSTITUTIONAL_DENSITY_COMPACT_MAX_PX = 140;

export function densityModeForSlotWidth(slotWidthPx: number): InstitutionalHoldersChartDensityMode {
  if (slotWidthPx < INSTITUTIONAL_DENSITY_MINIMAL_MAX_PX) return 'minimal';
  if (slotWidthPx < INSTITUTIONAL_DENSITY_COMPACT_MAX_PX) return 'compact';
  return 'full';
}

export function maxInstitutionsForDensityMode(
  mode: InstitutionalHoldersChartDensityMode
): number {
  switch (mode) {
    case 'full':
      return Number.POSITIVE_INFINITY;
    case 'compact':
      return 3;
    case 'minimal':
      return 0;
  }
}

export function columnWidthForSlot(
  slotWidthPx: number,
  mode: InstitutionalHoldersChartDensityMode
): number {
  const pad = mode === 'minimal' ? 4 : 8;
  const maxW =
    mode === 'full'
      ? INSTITUTIONAL_HOLDERS_COLUMN_WIDTH_PX
      : Math.min(INSTITUTIONAL_HOLDERS_COLUMN_WIDTH_PX, slotWidthPx);
  const minW = mode === 'minimal' ? 36 : mode === 'compact' ? 52 : 80;
  return Math.max(minW, Math.min(maxW, slotWidthPx - pad));
}

function barCenterX(
  xScale: (v: string) => number,
  bw: number,
  chartDate: string
): number | null {
  const x0 = xScale(chartDate);
  if (x0 == null || Number.isNaN(Number(x0))) return null;
  return Number(x0) + (bw > 0 ? bw / 2 : 0);
}

/**
 * Horizontal span of the quarter band on the plot (same bounds as column centering).
 */
export function quarterColumnSlotWidthPx(
  xScale: (v: string) => number,
  bw: number,
  filingPeriods: FilingPeriodForChart[],
  index: number,
  visibleChartStartDate?: string | null,
  quarterEndChartDatesAsc?: string[]
): number | null {
  const period = filingPeriods[index];
  if (!period) return null;

  const endDate = period.chartDate;
  let startDate: string;
  if (index > 0) {
    startDate = filingPeriods[index - 1]!.chartDate;
  } else {
    const priorEnds = (quarterEndChartDatesAsc ?? []).filter((d) => d < period.chartDate);
    if (priorEnds.length > 0) {
      startDate = priorEnds[priorEnds.length - 1]!;
    } else {
      const viewportStart = visibleChartStartDate?.trim() || null;
      startDate =
        viewportStart && period.chartStartDate < viewportStart
          ? viewportStart
          : period.chartStartDate;
    }
  }

  const startCx = barCenterX(xScale, bw, startDate);
  const endCx = barCenterX(xScale, bw, endDate);
  if (startCx == null || endCx == null) return null;
  return Math.abs(endCx - startCx);
}

export interface QuarterColumnLayout {
  slotWidthPx: number;
  densityMode: InstitutionalHoldersChartDensityMode;
  columnWidthPx: number;
  maxInstitutions: number;
}

export function layoutForQuarterColumn(
  slotWidthPx: number
): Omit<QuarterColumnLayout, 'slotWidthPx'> {
  const densityMode = densityModeForSlotWidth(slotWidthPx);
  return {
    densityMode,
    columnWidthPx: columnWidthForSlot(slotWidthPx, densityMode),
    maxInstitutions: maxInstitutionsForDensityMode(densityMode),
  };
}

/** True when any quarter column would render in minimal mode (for UI hints). */
export function overlayUsesMinimalDensity(
  slotWidthsPx: number[]
): boolean {
  return slotWidthsPx.some((w) => densityModeForSlotWidth(w) === 'minimal');
}
